// @mui
import { useSettingsContext } from 'src/components/settings';
import { useEffect, useState } from 'react';
import {
  Card,
  CardContent,
  Box,
  Paper,
  TextField,
  Grid,
  Stack,
  Button,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';

// components
import Iconify from 'src/components/iconify';
import { postRequestForWebsite } from 'src/services';
import { endpoints } from 'src/utils/axios';
import { handleErrorMessages } from 'src/services/fetching';
import { useSelector } from 'react-redux';
import useHeroSection from 'src/hooks/use-homepage-data';
import { useSnackbar } from 'notistack';

// ----------------------------------------------------------------------

export default function CommunitySection() {
  const settings = useSettingsContext();
  const [loading, setLoading] = useState(false);
  const homePageData=useSelector((state) => state.website.homePage);
  const {fetchOnMount}=useHeroSection();
  const { enqueueSnackbar } = useSnackbar();

  const [formData, setFormData] = useState({
    // Download Section
    downloadSection: {
      title: "",
      titleColor: "#333333",
      backgroundColor: "#ffffff",
      apps: [
        {
          platform: "",
          label: "",
          subLabel: "",
          icon: "apple",
          backgroundColor: "#8BC34A",
          textColor: "#ffffff",
          link: "/app-store"
        },
        {
          platform: "",
          label: "",
          subLabel: "",
          icon: "play",
          backgroundColor: "#8BC34A",
          textColor: "#ffffff",
          link: "/google-play"
        },
        {
          platform: "",
          label: "",
          subLabel: "",
          icon: "",
          backgroundColor: "#8BC34A",
          textColor: "#ffffff",
          link: "/web-app"
        }
      ]
    },

    // Stats Section
    statsSection: {
      title: "",
      titleColor: "#333333",
      backgroundColor: "#ffffff",
      stats: [
        {
          number: "",
          suffix: "",
          label: "",
          numberColor: "#8BC34A",
          labelColor: "#333333",
          backgroundColor: "#E8F5E8"
        },
        {
          number: "",
          suffix: "",
          label: "",
          numberColor: "#8BC34A",
          labelColor: "#333333",
          backgroundColor: "#E8F5E8"
        },
        {
          number: "",
          suffix: "",
          label: "",
          numberColor: "#8BC34A",
          labelColor: "#333333",
          backgroundColor: "#E8F5E8"
        },
        {
          number: "",
          suffix: "",
          label: "",
          numberColor: "#8BC34A",
          labelColor: "#333333",
          backgroundColor: "#E8F5E8"
        }
      ]
    },

    // Global Colors
    primaryGreen: "#8BC34A",
    accentGreen: "#4CAF50",
    lightGreen: "#E8F5E8"
  });

    useEffect(() => {
      fetchOnMount();
      }, []);
    useEffect(() => {
      if (homePageData?.community_section) {
        // Create a deep copy of the community section to avoid read-only property issues
        const communitySection = JSON.parse(JSON.stringify(homePageData.community_section));
        setFormData(prev => ({
          ...prev,
          ...communitySection
        }));
      }
    }, [homePageData])

  const updateFormData = (path, value) => {
    setFormData(prev => {
      // Create a deep copy of the previous state
      const newData = JSON.parse(JSON.stringify(prev));
      const keys = path.split('.');
      let current = newData;

      for (let i = 0; i < keys.length - 1; i++) {
        if (!current[keys[i]]) current[keys[i]] = {};
        current = current[keys[i]];
      }
      current[keys[keys.length - 1]] = value;
      return newData;
    });
  };

  const updateApp = (index, field, value) => {
    setFormData(prev => {
      // Create a deep copy of the previous state
      const newData = JSON.parse(JSON.stringify(prev));

      // Update the specific field
      newData.downloadSection.apps[index][field] = value;

      return newData;
    });
  };

  const updateStat = (index, field, value) => {
    setFormData(prev => {
      // Create a deep copy of the previous state
      const newData = JSON.parse(JSON.stringify(prev));

      // Update the specific field
      newData.statsSection.stats[index][field] = value;

      return newData;
    });
  };

  const addApp = () => {
    setFormData(prev => ({
      ...prev,
      downloadSection: {
        ...prev.downloadSection,
        apps: [...prev.downloadSection.apps, {
          platform: "New Platform",
          label: "Download on",
          subLabel: "Platform",
          icon: "web",
          backgroundColor: "#8BC34A",
          textColor: "#ffffff",
          link: "/new-platform"
        }]
      }
    }));
  };

  const removeApp = (index) => {
    setFormData(prev => ({
      ...prev,
      downloadSection: {
        ...prev.downloadSection,
        apps: prev.downloadSection.apps.filter((_, i) => i !== index)
      }
    }));
  };

  const addStat = () => {
    setFormData(prev => ({
      ...prev,
      statsSection: {
        ...prev.statsSection,
        stats: [...prev.statsSection.stats, {
          number: "0",
          suffix: "+",
          label: "New Metric",
          numberColor: "#8BC34A",
          labelColor: "#333333",
          backgroundColor: "#E8F5E8"
        }]
      }
    }));
  };

  const removeStat = (index) => {
    setFormData(prev => ({
      ...prev,
      statsSection: {
        ...prev.statsSection,
        stats: prev.statsSection.stats.filter((_, i) => i !== index)
      }
    }));
  };


   const handleSave = async () => {
     setLoading(true);

     try {
       // Create a FormData object for file uploads
       const formDataToSend = new FormData();

       // Append all form data
       Object.entries(formData).forEach(([key, value]) => {
         // Skip heroImage as we'll handle it separately
         if (key !== 'heroImage') {
           // Handle nested objects like primaryButton and secondaryButton
           if (typeof value === 'object' && value !== null && !(value instanceof File)) {
             formDataToSend.append(key, JSON.stringify(value));
           } else {
             formDataToSend.append(key, value);
           }
         }
       });

       // Append the hero image if it exists
       if (formData.heroImage) {
         formDataToSend.append('heroImage', formData.heroImage);
       }

       const response = await postRequestForWebsite(
         endpoints.admin_dashboard.website.home_page.hero_section,
         // formDataToSend,
         // dataForHeroSection,
         {
           attributes:{
            ...JSON.parse(JSON.stringify(homePageData)),
             community_section: JSON.parse(JSON.stringify(formData))
           }
         },
         true
       );

       if (response.metadata?.success) {
         enqueueSnackbar({
           variant: 'success',
           message: response.metadata.message,
         });
       } else {
         handleErrorMessages(response.message || 'An error occurred.');
       }
     } catch (err) {
       console.error('Error:', err);
       handleErrorMessages(err.message || 'Submission failed.');
     } finally {
       setLoading(false);
     }
   };

  const ColorPreview = ({ color, size = 24 }) => (
    <Box
      sx={{
        width: size,
        height: size,
        borderRadius: '4px',
        backgroundColor: color,
        border: '1px solid #ddd',
        display: 'inline-block',
        mr: 1
      }}
    />
  );

  const getIconComponent = (iconType) => {
    switch (iconType) {
      case 'apple': return '';
      case 'play': return '▶';
      case 'web': return '';
      default: return '';
    }
  };

  return (
    <Card>
      <CardContent>

        {/* Download Section */}
        <Box sx={{ mb: 5, pb: 3, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Stack direction="row" alignItems="center" sx={{ mb: 3 }}>
            <Box sx={{ width: 4, height: 20, bgcolor: 'primary.main', borderRadius: 1, mr: 1.5 }} />
            <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
              Community Section
            </Typography>
          </Stack>

          {/* Download Section Title */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Section Title</Typography>
            <TextField
              fullWidth
              value={formData.downloadSection.title}
              onChange={(e) => updateFormData('downloadSection.title', e.target.value)}
              placeholder="Enter section title"
              sx={{ mb: 2 }}
            />
            <Stack direction="row" alignItems="center" spacing={2}>
              <Typography variant="body2">Title Color:</Typography>
              <ColorPreview color={formData.downloadSection.titleColor} />
              <TextField
                type="color"
                value={formData.downloadSection.titleColor}
                onChange={(e) => updateFormData('downloadSection.titleColor', e.target.value)}
                sx={{ width: 80 }}
              />
            </Stack>
          </Box>

          {/* App Download Buttons */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 2 }}>App Download Buttons</Typography>
            {formData.downloadSection.apps.map((app, index) => (
              <Paper key={index} variant="outlined" sx={{ p: 3, mb: 3, bgcolor: 'background.neutral' }}>
                <Grid container spacing={3}>
                  {/* Left Column - Content */}
                  <Grid item xs={12} lg={6}>
                    <Stack spacing={2}>
                      <TextField
                        fullWidth
                        label="Platform Name"
                        value={app.platform}
                        onChange={(e) => updateApp(index, 'platform', e.target.value)}
                      />
                      <Grid container spacing={2}>
                        <Grid item xs={6}>
                          <TextField
                            fullWidth
                            label="Label"
                            value={app.label}
                            onChange={(e) => updateApp(index, 'label', e.target.value)}
                            placeholder="Download on"
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <TextField
                            fullWidth
                            label="Sub Label"
                            value={app.subLabel}
                            onChange={(e) => updateApp(index, 'subLabel', e.target.value)}
                            placeholder="App Store"
                          />
                        </Grid>
                      </Grid>
                      <TextField
                        fullWidth
                        label="Link URL"
                        value={app.link}
                        onChange={(e) => updateApp(index, 'link', e.target.value)}
                        placeholder="/app-store"
                      />
                    </Stack>
                  </Grid>

                  {/* Right Column - Styling */}
                  <Grid item xs={12} lg={6}>
                    <Stack spacing={2}>
                      <FormControl fullWidth>
                        <InputLabel>Icon Type</InputLabel>
                        <Select
                          value={app.icon}
                          label="Icon Type"
                          onChange={(e) => updateApp(index, 'icon', e.target.value)}
                        >
                          <MenuItem value="apple">Apple</MenuItem>
                          <MenuItem value="play">Google Play</MenuItem>
                          <MenuItem value="web">Web</MenuItem>
                        </Select>
                      </FormControl>

                      <Grid container spacing={2}>
                        <Grid item xs={6}>
                          <Typography variant="body2" sx={{ mb: 1 }}>Background Color</Typography>
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <ColorPreview color={app.backgroundColor} />
                            <TextField
                              type="color"
                              value={app.backgroundColor}
                              onChange={(e) => updateApp(index, 'backgroundColor', e.target.value)}
                              sx={{ width: 60 }}
                            />
                          </Stack>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="body2" sx={{ mb: 1 }}>Text Color</Typography>
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <ColorPreview color={app.textColor} />
                            <TextField
                              type="color"
                              value={app.textColor}
                              onChange={(e) => updateApp(index, 'textColor', e.target.value)}
                              sx={{ width: 60 }}
                            />
                          </Stack>
                        </Grid>
                      </Grid>
                    </Stack>
                  </Grid>
                </Grid>

                {/* Remove Button */}
                {/* <Stack direction="row" justifyContent="flex-end" sx={{ mt: 2 }}>
                  <Button
                    variant="contained"
                    color="error"
                    size="small"
                    startIcon={<Iconify icon="mingcute:close-line" />}
                    onClick={() => removeApp(index)}
                  >
                    Remove
                  </Button>
                </Stack> */}
              </Paper>
            ))}

            {/* <Button
              variant="contained"
              color="success"
              startIcon={<Iconify icon="mingcute:add-line" />}
              onClick={addApp}
            >
              Add Download Button
            </Button> */}
          </Box>
        </Box>

        {/* Stats Section */}
        <Box sx={{ mb: 5, pb: 3, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Stack direction="row" alignItems="center" sx={{ mb: 3 }}>
            <Box sx={{ width: 4, height: 20, bgcolor: 'primary.main', borderRadius: 1, mr: 1.5 }} />
            <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
              <Iconify icon="eva:people-fill" sx={{ mr: 1 }} />
              Statistics Section
            </Typography>
          </Stack>

          {/* Stats Section Title */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Section Title</Typography>
            <TextField
              fullWidth
              value={formData.statsSection.title}
              onChange={(e) => updateFormData('statsSection.title', e.target.value)}
              placeholder="Enter section title"
              sx={{ mb: 2 }}
            />
            <Stack direction="row" alignItems="center" spacing={2}>
              <Typography variant="body2">Title Color:</Typography>
              <ColorPreview color={formData.statsSection.titleColor} />
              <TextField
                type="color"
                value={formData.statsSection.titleColor}
                onChange={(e) => updateFormData('statsSection.titleColor', e.target.value)}
                sx={{ width: 80 }}
              />
            </Stack>
          </Box>

          {/* Statistics */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 2 }}>Statistics</Typography>
            <Grid container spacing={3}>
              {formData.statsSection.stats.map((stat, index) => (
                <Grid item xs={12} lg={6} key={index}>
                  <Paper variant="outlined" sx={{ p: 3, bgcolor: 'background.neutral' }}>
                    <Stack spacing={2}>
                      <Grid container spacing={2}>
                        <Grid item xs={8}>
                          <TextField
                            fullWidth
                            label="Number"
                            value={stat.number}
                            onChange={(e) => updateStat(index, 'number', e.target.value)}
                            placeholder="500"
                          />
                        </Grid>
                        <Grid item xs={4}>
                          <TextField
                            fullWidth
                            label="Suffix"
                            value={stat.suffix}
                            onChange={(e) => updateStat(index, 'suffix', e.target.value)}
                            placeholder="+"
                          />
                        </Grid>
                      </Grid>

                      <TextField
                        fullWidth
                        label="Label"
                        value={stat.label}
                        onChange={(e) => updateStat(index, 'label', e.target.value)}
                        placeholder="Product Features"
                      />

                      <Grid container spacing={2}>
                        <Grid item xs={4}>
                          <Typography variant="body2" sx={{ mb: 1 }}>Number Color</Typography>
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <ColorPreview color={stat.numberColor} size={20} />
                            <TextField
                              type="color"
                              value={stat.numberColor}
                              onChange={(e) => updateStat(index, 'numberColor', e.target.value)}
                              sx={{ width: 50 }}
                            />
                          </Stack>
                        </Grid>
                        <Grid item xs={4}>
                          <Typography variant="body2" sx={{ mb: 1 }}>Label Color</Typography>
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <ColorPreview color={stat.labelColor} size={20} />
                            <TextField
                              type="color"
                              value={stat.labelColor}
                              onChange={(e) => updateStat(index, 'labelColor', e.target.value)}
                              sx={{ width: 50 }}
                            />
                          </Stack>
                        </Grid>
                        {/* Remove Button */}
                        {/* <Grid item xs={4} >
                        <Stack direction="row" justifyContent="flex-end">
                          <Button
                            variant="contained"
                            color="error"
                            size="small"
                            startIcon={<Iconify icon="mingcute:close-line" />}
                            onClick={() => removeStat(index)}
                          >
                            Remove
                          </Button>
                        </Stack>
                        </Grid> */}
                        {/* <Grid item xs={4}>
                          <Typography variant="body2" sx={{ mb: 1 }}>Background</Typography>
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <ColorPreview color={stat.backgroundColor} size={20} />
                            <TextField
                              type="color"
                              value={stat.backgroundColor}
                              onChange={(e) => updateStat(index, 'backgroundColor', e.target.value)}
                              sx={{ width: 50 }}
                            />
                          </Stack>
                        </Grid> */}
                      </Grid>


                    </Stack>
                  </Paper>
                </Grid>
              ))}
            </Grid>

            {/* <Button
              variant="contained"
              color="success"
              startIcon={<Iconify icon="mingcute:add-line" />}
              onClick={addStat}
              sx={{ mt: 2 }}
            >
              Add Statistic
            </Button> */}
          </Box>
        </Box>

        {/* Action Buttons */}
        <Stack direction="row" justifyContent="flex-end" spacing={2} sx={{ pt: 3 }}>
          {/* <Button
            variant="outlined"
            color="inherit"
            startIcon={<Iconify icon="eva:file-text-fill" />}
            onClick={() => handleSave(true)}
          >
            Save as Draft
          </Button> */}
          <Button
            variant="contained"
            color="primary"
            startIcon={<Iconify icon="eva:save-fill" />}
            onClick={() => handleSave(false)}
          >
            Update
          </Button>
        </Stack>
      </CardContent>
    </Card>
  );
}
